import { FormHelperText, Grid } from '@mui/material';
import { SendCodeButton } from '@/components/general/buttons/SendCodeButton/SendCodeButton';
import { ReactElement, ReactNode, useState } from 'react';
import { GridSize } from '@/types/types';
import { useConfigStore } from '@/store/configStore/configStore';

interface WithSendCodeProps {
    hasSendCode: boolean;
    children: ReactNode;
    onSendCode: () => Promise<boolean | { secondsLeft: number }>;
    codeType?: 'email' | 'phone';
    codeFormInput: ReactElement;
    buttonBreakpoint?: GridSize;
    errorMessage?: string;
    buttonLabel?: string;
}

export const WithSendCode = (props: WithSendCodeProps) => {
    const { website_2fa_email_resend_code_timeout, website_sms_resend_code_timeout } =
        useConfigStore((state) => ({
            website_2fa_email_resend_code_timeout: state.website_2fa_email_resend_code_timeout,
            website_sms_resend_code_timeout: state.website_sms_resend_code_timeout,
        }));

    const timerStartTime =
        props.codeType === 'phone'
            ? website_sms_resend_code_timeout
            : website_2fa_email_resend_code_timeout;

    const [refreshInterval, setRefreshInterval] = useState<number>(0);

    const handleSendCodeButtonClick = async () => {
        const codeSent = await props.onSendCode();

        if (typeof codeSent === 'object' && codeSent !== null && 'secondsLeft' in codeSent) {
            setRefreshInterval(codeSent.secondsLeft);
            return false;
        }

        if (codeSent === true) {
            setRefreshInterval(timerStartTime);
        }

        return codeSent;
    };

    return (
        <>
            <Grid container>
                <Grid size={12}>{props.children}</Grid>
            </Grid>
            {props.hasSendCode && (
                <Grid container sx={{ pt: 1.75 }} data-testid="sendcode-input">
                    <Grid
                        size={props.buttonBreakpoint ?? 7}
                        sx={{
                            paddingRight: 2,
                        }}
                    >
                        <SendCodeButton
                            codeType={props.codeType}
                            buttonProps={{ fullWidth: true, variant: 'outlined' }}
                            onClick={handleSendCodeButtonClick}
                            refreshInterval={refreshInterval}
                        >
                            {props.buttonLabel ? props.buttonLabel : undefined}
                        </SendCodeButton>
                    </Grid>
                    <Grid size={12 - (props.buttonBreakpoint ?? 7)}>{props.codeFormInput}</Grid>
                    {props.errorMessage && (
                        <Grid size={12}>
                            <FormHelperText error>{props.errorMessage}</FormHelperText>
                        </Grid>
                    )}
                </Grid>
            )}
        </>
    );
};
